import React, { useEffect, useState } from 'react';
import { Spinner, Table, Form, Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import AdvanceTableWrapper from 'components/common/advance-table/AdvanceTableWrapper';
import { useToast } from 'context/providers/ToastProvider';
import useToken from 'hooks/useToken';
import akasiaLogo from "assets/img/illustrations/akasia.png";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

const PagosLista = ({ filters, setID_Pago, setShowModalConciliar, refreshTrigger }) => {
    const { token } = useToken();
    const { showErrorToast } = useToast();

    const [pagos, setPagos] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [searchDebounced, setSearchDebounced] = useState('');
    const [sortBy, setSortBy] = useState('dFechaPago');
    const [sortDir, setSortDir] = useState('DESC');

    useEffect(() => {
        const timer = setTimeout(() => setSearchDebounced(searchText.trim()), 400);
        return () => clearTimeout(timer);
    }, [searchText]);

    useEffect(() => {
        if (filters.month && filters.year) {
            ObtenerPagosList(pageNumber, pageSize, searchDebounced);
        }
    }, [
        filters.month,
        filters.year,
        filters.idCatCuentaBancaria,
        filters.idCatMedioPago,
        filters.idVendedor,
        filters.filterValidados,
        filters.filterStatus,
        searchDebounced,
        sortBy,
        sortDir,
        refreshTrigger
    ]);

    const handlePageChange = (page) => {
        setPageNumber(page);
        ObtenerPagosList(page, pageSize, searchDebounced);
    };

    const handlePageSizeChange = (e) => {
        const size = parseInt(e.target.value);
        setPageSize(size);
        setPageNumber(1);
        ObtenerPagosList(1, size, searchDebounced);
    };

    const ObtenerPagosList = async (page = 1, size = pageSize, search = searchDebounced) => {
        setCargando(true);

        const url = `/api/AdministracionPagos/ObtenerPagosList/?month=${filters.month}&year=${filters.year}&idCatCuentaBancaria=${filters.idCatCuentaBancaria}&idMedioPago=${filters.idCatMedioPago}&idVendedor=${filters.idVendedor}&iValidado=${filters.filterValidados}&iEstatus=${filters.filterStatus}&pageNumber=${page}&pageSize=${size}&searchText=${encodeURIComponent(search)}&sortBy=${sortBy}&sortDir=${sortDir}`;

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            }
        }).catch((error) => {
            showErrorToast('' + error.message);
        });

        if (response && response.ok) {
            const objResponse = await response.json();
            if (objResponse.iResponseType == 1) {
                const data = JSON.parse(objResponse.detalles);
                const table2 = data['table2'] || data['Table2'] || [];
                const table3 = data['table3'] || data['Table3'] || [];

                setPagos(table2);

                if (table3.length > 0 && table3[0].TotalRegistros !== undefined) {
                    setTotalRegistros(table3[0].TotalRegistros);
                } else {
                    setTotalRegistros(0);
                }
            } else {
                showErrorToast(objResponse.vMessage);
            }
        }
        setCargando(false);
    };

    const renderSortableHeader = (label, columnSortBy) => (
        <div
            style={{ cursor: 'pointer', display: 'inline-block' }}
            onClick={() => {
                if (sortBy === columnSortBy) {
                    setSortDir(sortDir === 'DESC' ? 'ASC' : 'DESC');
                } else {
                    setSortBy(columnSortBy);
                    setSortDir('ASC');
                }
            }}
        >
            {label}
            <span className="ms-2">
                {sortBy === columnSortBy ? (
                    sortDir === 'DESC' ? <FontAwesomeIcon icon="sort-down" className="text-primary" /> : <FontAwesomeIcon icon="sort-up" className="text-primary" />
                ) : <FontAwesomeIcon icon="sort" className="text-300" />}
            </span>
        </div>
    );

    const columns = [
        {
            accessor: 'actions',
            Header: '',
            Cell: rowData => {
                const { idPago } = rowData.row.original;
                return (
                    <Link onClick={() => { setID_Pago(idPago); setShowModalConciliar(true) }} className="text-500 text-decoration-none" title="Ver detalles del pago">
                        <FontAwesomeIcon icon="eye" />
                    </Link>
                );
            },
            disableSortBy: true
        },
        {
            accessor: 'idPago',
            Header: 'ID',
            Cell: rowData => (
                <span className="text-dark">{rowData.row.original.idPago}</span>
            ),
            disableSortBy: true
        },
        {
            accessor: 'none',
            Header: 'Estatus',
            Cell: rowData => {
                const { bIsActive, bIsFacturado, idCatCuentaBancaria } = rowData.row.original;
                const estaFacturado = bIsFacturado === 1 || bIsFacturado === true;
                return (
                    <Row className="align-items-center gap-1 g-0 flex-nowrap w-auto">
                        <Col xs="auto"><img title={bIsActive == 1 ? "Pagado" : "No Pagado"} src={bIsActive == 1 ? "/img/activo.png" : "/img/noactivo.png"} alt="status" /></Col>
                        <Col xs="auto"><img title={idCatCuentaBancaria > 0 ? "Validado" : "No Validado"} src={idCatCuentaBancaria > 0 ? "/img/activo.png" : "/img/noactivo.png"} alt="validado" /></Col>
                        <Col xs="auto"><img title={estaFacturado ? "Facturado" : "No Facturado"} src={estaFacturado ? "/img/activo.png" : "/img/noactivo.png"} alt="facturado" /></Col>
                    </Row>
                );
            },
            disableSortBy: true
        },
        {
            accessor: 'idOrden',
            Header: 'Orden',
            Cell: rowData => {
                const { idOrden, vNombreCliente, bIsActive, bIsRecompra, bIsDistribucion } = rowData.row.original;
                return (
                    <Row className="flex-column align-items-end align-items-md-start g-0 w-auto text-end text-md-start" style={{ maxWidth: '200px' }}>
                        <Col xs={12}>
                            <Link to={"/ordenes/detalle/" + idOrden} className={classNames("fw-bold text-truncate", { "text-danger": bIsActive == false })}>
                                #{idOrden}
                            </Link>
                        </Col>
                        <Col xs={12}>
                            <span className={classNames("text-600 text-truncate d-block", { "text-danger text-decoration-line-through": bIsActive == false })}>
                                {bIsDistribucion == null ? "" : bIsDistribucion == 1 ? "🎖️ " : bIsDistribucion == 0 ? <img width="12" src={akasiaLogo} alt="logo" className="me-1" /> : ""}
                                {bIsRecompra == 1 && "♻ "}
                                {vNombreCliente}
                            </span>
                        </Col>
                    </Row>
                );
            },
            disableSortBy: true
        },
        {
            accessor: 'dFechaPago',
            Header: () => renderSortableHeader('Fecha Pago', 'dFechaPago'),
            Cell: rowData => (
                <span className="text-dark">
                    {rowData.row.original.dFechaPagoLargo}
                </span>
            ),
            disableSortBy: true
        },
        {
            accessor: 'vUsuario',
            Header: 'Vendedor',
            Cell: rowData => (
                <span className="text-dark text-uppercase text-truncate d-inline-block" style={{ maxWidth: '80px' }}>
                    {rowData.row.original.vUsuario}
                </span>
            ),
            disableSortBy: true
        },
        {
            accessor: 'dImportePago',
            Header: () => renderSortableHeader('Importe Pago', 'dImportePago'),
            Cell: rowData => (
                <span className="fw-bold text-dark text-end d-block">
                    ${parseFloat(rowData.row.original.dImportePago).toLocaleString("es-MX", { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
                </span>
            ),
            disableSortBy: true
        },
        {
            accessor: 'vMoneda',
            Header: 'Moneda',
            Cell: rowData => {
                const { vMoneda, dTipoCambio } = rowData.row.original;
                return (
                    <span className="text-500" style={{ fontSize: '0.8rem' }}>
                        {vMoneda} {vMoneda == "MXN" ? "" : " T.C. " + parseFloat(dTipoCambio).toFixed(4)}
                    </span>
                );
            },
            disableSortBy: true
        },
        {
            accessor: 'vReferencia',
            Header: 'Referencia',
            Cell: rowData => (
                rowData.row.original.vReferencia ?
                    <span className="text-primary fw-semi-bold text-truncate d-inline-block" style={{ maxWidth: '100px' }} title={rowData.row.original.vReferencia}>
                        {rowData.row.original.vReferencia}
                    </span>
                    : <span className="text-400">-</span>
            ),
            disableSortBy: true
        },
        {
            accessor: 'vMedioPago',
            Header: () => renderSortableHeader('Medio de Pago', 'vMedioPago'),
            Cell: rowData => {
                const { vMedioPago } = rowData.row.original;
                return (
                    <Row className="align-items-center gap-1 fw-semi-bold text-700 g-0 flex-nowrap w-auto">
                        {vMedioPago == 'EFECTIVO' && <Col xs="auto"><img src="/img/efectivo20.png" alt="efectivo" width="16" /></Col>}
                        {vMedioPago == 'STRIPE' && <Col xs="auto"><img src="/img/stripe30.png" alt="stripe" width="24" /></Col>}
                        {vMedioPago == 'PAYPAL' && <Col xs="auto"><img src="/img/paypal20.png" alt="paypal" width="16" /></Col>}
                        {vMedioPago == 'BANCO' && <Col xs="auto"><img src="/img/transferencia20.png" alt="banco" width="16" /></Col>}
                        <Col xs="auto"><span className="text-truncate d-inline-block" style={{ maxWidth: '60px' }}>{vMedioPago}</span></Col>
                    </Row>
                );
            },
            disableSortBy: true
        },
        {
            accessor: 'vDescripcionFormaPago',
            Header: () => renderSortableHeader('Forma de Pago', 'vDescripcionFormaPago'),
            Cell: rowData => {
                const { vClave, vDescripcionFormaPago } = rowData.row.original;
                return (
                    <Row className="align-items-center gap-1 fw-semi-bold text-600 g-0 flex-nowrap w-auto">
                        {vClave == '01' && <Col xs="auto"><img src="/img/efectivo20.png" alt="fp" width="16" /></Col>}
                        {vClave == '03' && <Col xs="auto"><img src="/img/transferencia20.png" alt="fp" width="16" /></Col>}
                        {vClave == '99' && <Col xs="auto"><img src="/img/pordefinir20.png" alt="fp" width="16" /></Col>}
                        {vClave == '28' && <Col xs="auto"><img src="/img/tarjeta20.png" alt="fp" width="16" /></Col>}
                        {vClave == '04' && <Col xs="auto"><img src="/img/tarjeta20.png" alt="fp" width="16" /></Col>}
                        {vClave == '31' && <Col xs="auto"><img src="/img/intermediario20.png" alt="fp" width="16" /></Col>}
                        <Col xs="auto">
                            <span className="text-truncate d-inline-block" style={{ maxWidth: '100px' }} title={`${vClave} - ${vDescripcionFormaPago}`}>
                                {vClave} - {vDescripcionFormaPago}
                            </span>
                        </Col>
                    </Row>
                );
            },
            disableSortBy: true
        }
    ];

    const totalPages = totalRegistros > 0 ? Math.ceil(totalRegistros / pageSize) : 0;



    return (
        <Row className="bg-white rounded-4 shadow-sm border border-200 overflow-hidden m-0">
            <Col xs={12} className="p-0">
                <AdvanceTableWrapper
                    columns={columns}
                    data={pagos}
                    sortable
                    pagination={true}
                    perPage={100000}
                >
                    <Row className="justify-content-end align-items-center px-4 py-3 border-bottom border-200 m-0">
                        <Col xs={12} sm={10} md={8} lg={6} xl={5} style={{ position: 'relative' }} className="p-0">
                            <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Buscar por ID, Orden, Referencia..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                autoComplete="off"
                                style={{ paddingRight: '30px', paddingLeft: '30px' }}
                            />
                            <div style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', display: 'flex', color: '#a0a0a0', pointerEvents: 'none' }}>
                                <FontAwesomeIcon icon={faSearch} />
                            </div>
                            {cargando && (
                                <div style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', display: 'flex', pointerEvents: 'none' }}>
                                    <Spinner animation="border" size="sm" variant="primary" />
                                </div>
                            )}
                        </Col>
                    </Row>

                    <Row className="p-0 position-relative m-0" style={{ minHeight: '200px' }}>
                        <Col xs={12} className="p-0">
                            <CustomTable table cargando={cargando} />
                        </Col>
                    </Row>

                    {(!cargando && totalRegistros > 0) && (
                        <Row className="justify-content-between align-items-center px-4 py-4 border-top border-200 bg-white m-0">
                            <Col xs={12} md="auto" className="d-flex justify-content-center justify-content-md-start align-items-center mb-3 mb-md-0 p-0">
                                <span className="fs--1 text-500 me-2">Mostrar</span>
                                <Form.Select size="sm" value={pageSize} onChange={handlePageSizeChange} className="w-auto me-2 border-300 shadow-none py-1" style={{ cursor: 'pointer', fontSize: '0.8rem' }}>
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={15}>15</option>
                                    <option value={30}>30</option>
                                    <option value={50}>50</option>
                                </Form.Select>
                                <span className="fs--1 text-500">
                                    de {totalRegistros} registros
                                </span>
                            </Col>
                            <Col xs={12} md="auto" className="d-flex justify-content-center align-items-center gap-2 p-0">
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => handlePageChange(pageNumber - 1)}
                                    className={classNames('bg-white text-600 border-200', { disabled: pageNumber === 1 })}
                                >
                                    Anterior
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => handlePageChange(pageNumber + 1)}
                                    className={classNames('bg-white text-600 border-200', { disabled: pageNumber === totalPages || totalPages === 0 })}
                                >
                                    Siguiente
                                </Button>
                            </Col>
                        </Row>
                    )}
                </AdvanceTableWrapper>
            </Col>
        </Row>
    );
};

PagosLista.propTypes = {
    filters: PropTypes.object.isRequired,
    setID_Pago: PropTypes.func.isRequired,
    setShowModalConciliar: PropTypes.func.isRequired,
    refreshTrigger: PropTypes.number
};

export default PagosLista;

const CustomTable = ({ getTableProps, headers, page, prepareRow, cargando }) => {
    return (
        <>
            {/* MOBILE CARD VIEW */}
            <Row className="d-md-none g-3 px-3 py-3 m-0 w-100">
                {cargando && page.length === 0 ? (
                    <Col xs={12} className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <h5 className="mt-2 text-600">Cargando pagos...</h5>
                    </Col>
                ) : !cargando && page.length === 0 ? (
                    <Col xs={12} className="text-center py-5">
                        <h5 className="text-500 mb-0">No se encontraron resultados</h5>
                    </Col>
                ) : (
                    page.map((row, i) => {
                        prepareRow(row);
                        return (
                            <Col xs={12} key={i}>
                                <Card className="border border-200 shadow-sm rounded-4">
                                    <Card.Body className="p-3 p-sm-4">
                                        <Row className="align-items-center mb-3 pb-3 border-bottom border-200 g-0 flex-nowrap">
                                            <Col className="fw-bold fs-2 text-700 text-truncate me-2" style={{ letterSpacing: '-1px' }}>
                                                Pago #{row.original.idPago}
                                            </Col>
                                            <Col xs="auto" className="d-flex justify-content-end align-items-center gap-2">
                                                {row.cells[2].render('Cell')}
                                                <div className="bg-light shadow-sm rounded-circle d-flex justify-content-center align-items-center border border-200" style={{ width: '35px', height: '35px', cursor: 'pointer' }}>
                                                    {row.cells[0].render('Cell')}
                                                </div>
                                            </Col>
                                        </Row>

                                        <Row className="mb-3 align-items-start g-0 flex-nowrap">
                                            <Col xs={5} className="text-500 fs--1 fw-semi-bold pt-1">Orden / Cliente</Col>
                                            <Col xs={7} className="text-end text-800 fs--1 d-flex justify-content-end pe-3 pe-sm-0">
                                                {row.cells[3].render('Cell')}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3 align-items-center g-0 flex-nowrap">
                                            <Col xs={5} className="text-500 fs--1 fw-semi-bold">Importe</Col>
                                            <Col xs={7} className="text-end text-800 fs--1 d-flex justify-content-end align-items-center gap-1 pe-3 pe-sm-0">
                                                {row.cells[6].render('Cell')} {row.cells[7].render('Cell')}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3 align-items-center g-0 flex-nowrap">
                                            <Col xs={5} className="text-500 fs--1 fw-semi-bold">Fecha</Col>
                                            <Col xs={7} className="text-end text-800 fs--1 d-flex justify-content-end pe-3 pe-sm-0">
                                                {row.cells[4].render('Cell')}
                                            </Col>
                                        </Row>

                                        <Row className="mb-3 align-items-center g-0 flex-nowrap">
                                            <Col xs={5} className="text-500 fs--1 fw-semi-bold">Vendedor</Col>
                                            <Col xs={7} className="text-end text-800 fs--1 d-flex justify-content-end pe-3 pe-sm-0">
                                                {row.cells[5].render('Cell')}
                                            </Col>
                                        </Row>

                                        <Row className="align-items-start g-0 flex-nowrap">
                                            <Col xs={5} className="text-500 fs--1 fw-semi-bold pt-1">Medio / Forma</Col>
                                            <Col xs={7} className="text-end text-800 fs--1 d-flex flex-column align-items-end gap-2 pe-3 pe-sm-0">
                                                {row.cells[9].render('Cell')}
                                                {row.cells[10].render('Cell')}
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })
                )}
            </Row>

            {/* DESKTOP TABLE VIEW */}
            <Row className="d-none d-md-flex m-0 p-0">
                <Col xs={12} className="p-0 m-0">
                    <Table {...getTableProps()} hover responsive className="fs--1 mb-0 text-sans-serif border-bottom border-200">
                        <thead className="bg-transparent text-dark text-uppercase align-middle" style={{ position: 'sticky', top: 0, zIndex: 1, fontSize: '0.65rem', letterSpacing: '0.02em' }}>
                            <tr>
                                {headers.map((column, index) => (
                                    <th
                                        key={index}
                                        {...column.getHeaderProps(column.getSortByToggleProps(column.headerProps))}
                                        className={classNames('py-4 border-bottom border-200 fw-semi-bold', column.canSort && 'cursor-pointer', column.headerProps?.className)}
                                    >
                                        {column.render('Header')}
                                        {column.canSort && (
                                            <span className="ms-2">
                                                {column.isSorted ? (
                                                    column.isSortedDesc ? (
                                                        <FontAwesomeIcon icon="sort-down" className="text-primary" />
                                                    ) : (
                                                        <FontAwesomeIcon icon="sort-up" className="text-primary" />
                                                    )
                                                ) : (
                                                    <FontAwesomeIcon icon="sort" className="text-300" />
                                                )}
                                            </span>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="align-middle border-0">
                            {cargando && page.length === 0 ? (
                                <tr>
                                    <td colSpan={headers.length} className="text-center py-5">
                                        <Spinner animation="border" variant="primary" />
                                        <h5 className="mt-2 text-600">Cargando pagos...</h5>
                                    </td>
                                </tr>
                            ) : !cargando && page.length === 0 ? (
                                <tr>
                                    <td colSpan={headers.length} className="text-center py-5">
                                        <h5 className="text-500 mb-0">No se encontraron resultados</h5>
                                    </td>
                                </tr>
                            ) : (
                                page.map((row, i) => {
                                    prepareRow(row);
                                    return (
                                        <tr key={i} {...row.getRowProps()} className="border-bottom border-200 hover-bg-100 bg-white">
                                            {row.cells.map((cell, index) => (
                                                <td key={index} {...cell.getCellProps(cell.column.cellProps)} className="py-4 align-middle">
                                                    {cell.render('Cell')}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </Table>
                </Col>
            </Row>
        </>
    );
};

CustomTable.propTypes = {
    getTableProps: PropTypes.func.isRequired,
    headers: PropTypes.array.isRequired,
    page: PropTypes.array.isRequired,
    prepareRow: PropTypes.func.isRequired,
    cargando: PropTypes.bool.isRequired
};
